function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

class ProductUtilityDrawer {
  constructor(root) {
    this.root = root;
    this.drawer = root.querySelector('[data-product-utility-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('[data-product-utility-panel]');
    this.title = this.drawer.querySelector('[data-product-utility-title]');
    this.body = this.drawer.querySelector('[data-product-utility-body]');
    this.viewer = this.drawer.querySelector('[data-product-utility-viewer]');
    this.viewerPanel = this.drawer.querySelector('.product-utility-drawer__viewer-panel');
    this.viewerBody = this.drawer.querySelector('[data-product-utility-viewer-body]');
    this.viewerTitle = this.drawer.querySelector('[data-product-utility-viewer-title]');
    this.primaryCloseButton = this.drawer.querySelector('[data-product-utility-close]');
    this.stickyAtc = document.getElementById('sticky-atc');
    this.triggers = Array.from(root.querySelectorAll('[data-product-utility-trigger]'));
    this.closeButtons = Array.from(this.drawer.querySelectorAll('[data-product-utility-close]'));
    this.viewerCloseButtons = Array.from(this.drawer.querySelectorAll('[data-product-utility-viewer-close]'));
    this.lastTrigger = null;
    this.isOpen = false;
    this.isViewerOpen = false;
    this.closeTimer = null;
    this.bodyPaddingRight = '';

    if (this.drawer.parentNode !== document.body) {
      document.body.appendChild(this.drawer);
    }

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleBodyClick = this.handleBodyClick.bind(this);

    this.triggers.forEach((trigger) => trigger.addEventListener('click', this.handleTriggerClick));
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.viewerCloseButtons.forEach((button) => button.addEventListener('click', () => this.closeViewer()));
    this.body?.addEventListener('click', this.handleBodyClick);
  }

  handleTriggerClick(event) {
    const trigger = event.currentTarget;
    const sourceId = trigger.dataset.drawerContentId;
    const source = sourceId ? document.getElementById(sourceId) : null;
    if (!source) return;

    this.lastTrigger = trigger;
    this.title.textContent = trigger.dataset.drawerTitle || trigger.textContent.trim();
    this.drawer.dataset.kind = trigger.dataset.drawerKind || 'general';
    this.body.innerHTML = source.innerHTML;
    this.enhanceSizeContent();
    this.open();
  }

  open() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (!this.drawer.hidden && this.isOpen) return;

    this.drawer.hidden = false;
    this.drawer.dataset.state = 'closed';
    this.handleViewportChange();
    void this.panel.offsetHeight;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.drawer.dataset.state = 'open';
        this.isOpen = true;
        this.lockBodyScroll();
        document.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('resize', this.handleViewportChange);
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.handleViewportChange);
        }
        this.primaryCloseButton?.focus({ preventScroll: true });
      });
    });
  }

  close() {
    if (!this.isOpen) return;

    this.closeViewer(true);
    this.drawer.dataset.state = 'closed';
    this.isOpen = false;
    this.unlockBodyScroll();
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleViewportChange);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportChange);
    }

    this.closeTimer = window.setTimeout(() => {
      this.drawer.hidden = true;
      this.body.innerHTML = '';
      this.drawer.dataset.kind = 'general';
      this.drawer.style.removeProperty('--product-utility-drawer-bottom-offset');
      if (this.lastTrigger) this.lastTrigger.focus({ preventScroll: true });
    }, 260);
  }

  lockBodyScroll() {
    const scrollbarWidth = Math.max(window.innerWidth - document.documentElement.clientWidth, 0);

    this.bodyPaddingRight = document.body.style.paddingRight;
    document.body.classList.add('overflow-hidden');

    if (scrollbarWidth > 0) {
      const currentPaddingRight = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }
  }

  unlockBodyScroll() {
    document.body.classList.remove('overflow-hidden');
    document.body.style.paddingRight = this.bodyPaddingRight;
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      if (this.isViewerOpen) {
        this.closeViewer();
        return;
      }

      this.close();
    }
  }

  handleViewportChange() {
    const bottomOffset =
      window.visualViewport && window.innerWidth < 750
        ? Math.max(window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop, 0)
        : 0;
    let stickyOffset = 0;

    if (
      window.innerWidth < 750 &&
      this.stickyAtc &&
      this.stickyAtc.classList.contains('sticky-atc--visible')
    ) {
      stickyOffset = this.stickyAtc.offsetHeight;
    }

    this.drawer.style.setProperty('--product-utility-drawer-bottom-offset', `${bottomOffset}px`);
    this.drawer.style.setProperty('--product-utility-drawer-sticky-offset', `${stickyOffset}px`);
  }

  handleBodyClick(event) {
    var trigger = event.target.closest('[data-product-utility-viewer-trigger]');
    if (trigger) {
      event.preventDefault();
      this.openViewer({
        src: trigger.dataset.viewerSrc,
        type: trigger.dataset.viewerType,
        title: trigger.dataset.viewerTitle || this.title.textContent
      });
      return;
    }

    if (this.drawer.dataset.kind !== 'size') return;

    var link = event.target.closest('a[href]');
    if (!link) return;

    var assetType = this.getViewerType(link.href);
    if (!assetType) return;

    event.preventDefault();
    this.openViewer({
      src: link.href,
      type: assetType,
      title: link.textContent.trim() || this.title.textContent
    });
  }

  enhanceSizeContent() {
    if (!this.body) return;

    this.body.classList.remove('product-utility-drawer__body--size-guide');

    if (this.drawer.dataset.kind !== 'size') {
      return;
    }

    var assetLinks = Array.from(this.body.querySelectorAll('a[href]')).filter((link) => this.getViewerType(link.href));

    if (!assetLinks.length) {
      return;
    }

    this.body.classList.add('product-utility-drawer__body--size-guide');

    assetLinks.forEach((link) => {
      var assetType = this.getViewerType(link.href);
      var card = this.buildSizeGuideCard({
        href: link.href,
        type: assetType,
        title: link.textContent.trim() || 'Open size guide'
      });
      var paragraph = link.closest('p');

      if (
        paragraph &&
        paragraph.querySelectorAll('a').length === 1 &&
        paragraph.textContent.trim() === link.textContent.trim()
      ) {
        paragraph.replaceWith(card);
        return;
      }

      link.replaceWith(card);
    });
  }

  buildSizeGuideCard(config) {
    var card = document.createElement('button');
    var preview = '';

    card.type = 'button';
    card.className = 'product-utility-drawer__size-guide-card';
    card.dataset.productUtilityViewerTrigger = 'true';
    card.dataset.viewerSrc = config.href;
    card.dataset.viewerType = config.type;
    card.dataset.viewerTitle = config.title;

    if (config.type === 'image') {
      preview =
        '<span class="product-utility-drawer__size-guide-preview">' +
        '<img src="' +
        escapeHtml(this.buildPreviewUrl(config.href, 880)) +
        '" alt="" loading="lazy">' +
        '</span>';
    } else {
      preview =
        '<span class="product-utility-drawer__size-guide-file">' +
        '<span class="product-utility-drawer__size-guide-file-badge">PDF</span>' +
        '<span class="product-utility-drawer__size-guide-file-copy">Opens inside the store</span>' +
        '</span>';
    }

    card.innerHTML =
      '<span class="product-utility-drawer__size-guide-eyebrow">A4Y fit guide</span>' +
      '<span class="product-utility-drawer__size-guide-title">' +
      escapeHtml(config.title) +
      '</span>' +
      '<span class="product-utility-drawer__size-guide-copy">Open the full chart without leaving the product page.</span>' +
      preview +
      '<span class="product-utility-drawer__size-guide-cta">View size guide</span>';

    return card;
  }

  getViewerType(url) {
    if (!url) return null;

    try {
      var parsed = new URL(url, window.location.origin);
      var pathname = parsed.pathname.toLowerCase();

      if (/\.(png|apng|avif|gif|jpe?g|jfif|pjpeg|pjp|svg|webp)$/.test(pathname)) {
        return 'image';
      }

      if (/\.pdf$/.test(pathname)) {
        return 'pdf';
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  buildPreviewUrl(url, width) {
    if (!url) return '';

    if (url.indexOf('cdn.shopify.com') === -1 || !width) {
      return url;
    }

    return url + (url.indexOf('?') === -1 ? '?width=' + width : '&width=' + width);
  }

  openViewer(config) {
    if (!this.viewer || !this.viewerBody || !config || !config.src) {
      window.open(config.src, '_blank', 'noopener');
      return;
    }

    var title = config.title || 'Size guide';
    var src = config.src;
    var type = config.type || this.getViewerType(src) || 'image';

    this.viewerTitle.textContent = title;
    this.viewerBody.innerHTML =
      type === 'pdf'
        ? '<iframe src="' +
          escapeHtml(src) +
          '#view=FitH" title="' +
          escapeHtml(title) +
          '" loading="eager"></iframe>'
        : '<img src="' +
          escapeHtml(src) +
          '" alt="' +
          escapeHtml(title) +
          '" loading="eager">';

    this.viewer.hidden = false;

    requestAnimationFrame(() => {
      this.viewer.dataset.state = 'open';
      this.isViewerOpen = true;
      this.viewerPanel?.focus({ preventScroll: true });
    });
  }

  closeViewer(immediate) {
    if (!this.viewer || (!this.isViewerOpen && this.viewer.hidden)) return;

    this.viewer.dataset.state = 'closed';
    this.isViewerOpen = false;

    var finalize = () => {
      this.viewer.hidden = true;
      this.viewerBody.innerHTML = '';
    };

    if (immediate) {
      finalize();
      return;
    }

    window.setTimeout(finalize, 220);
  }
}

const initProductUtilityDrawers = (container = document) => {
  container.querySelectorAll('[data-product-utility-root]').forEach((root) => {
    if (root.dataset.productUtilityReady === 'true') return;
    root.dataset.productUtilityReady = 'true';
    new ProductUtilityDrawer(root);
  });
};

initProductUtilityDrawers();

document.addEventListener('shopify:section:load', (event) => {
  initProductUtilityDrawers(event.target);
});
